import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	ActionBar,
	Button,
	DeleteIcon,
	SpinnerWithBackground,
	Text,
	Toggle,
	UnsavedChangesDialog,
	useUnsavedChangesWarning,
} from '@viapeople/components'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { PopulationFields } from '../../../components/PopulationFields'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import { EnpsExternalParticipantsUploadModal } from '../components/EnpsExternalParticipantsUploadModal'
import { useEnpsStore } from '../store'

/**
 * @fileoverview eNPS Population Tab
 *
 * Select target population for eNPS surveys (internal users)
 * and manage external participants (CSV/Excel upload)
 */

interface PopulationFormData {
	population: {
		hierarchyLevels: number[]
		roles: Array<{ id: string; label: string }>
		blacklistedHierarchyRoles: Array<{ id: string; label: string }>
		whitelistedUsers: Array<{ id: string; externalId: string; label: string }>
		blacklistedUsers: Array<{ id: string; externalId: string; label: string }>
	}
}

export const EnpsPopulationTab = () => {
	const { organizationId, enpsId } = useParams<{
		organizationId: string
		enpsId: string
	}>()
	const navigate = useNavigate()
	const toast = useToast()
	const queryClient = useQueryClient()
	const { enpsConfig, useEditEnpsConfig } = useEnpsStore()
	const { editEnpsConfig, isLoading: isSaving } = useEditEnpsConfig(enpsId)

	// State for external participants
	const [allowExternalParticipants, setAllowExternalParticipants] = useState(
		enpsConfig?.allowExternalParticipants ?? false,
	)
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)

	const form = useForm<PopulationFormData>({
		defaultValues: {
			population: {
				hierarchyLevels: [],
				roles: [],
				blacklistedHierarchyRoles: [],
				whitelistedUsers: [],
				blacklistedUsers: [],
			},
		},
	})

	const {
		formState: { isDirty },
	} = form
	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	// Mutations for population operations
	const createPopulationMutation = useMutation({
		...trpc.admin.populations.create.mutationOptions(),
	})

	const editPopulationMutation = useMutation({
		...trpc.admin.populations.edit.mutationOptions(),
	})

	// Fetch existing population data if populationId exists
	const { data: populationData, isLoading: isLoadingPopulation } = useQuery({
		...trpc.admin.populations.getFullById.queryOptions({
			id: enpsConfig?.populationId || '',
		}),
		enabled: !!enpsConfig?.populationId,
	})

	// Fetch config external participants
	const { data: externalParticipants, isLoading: isLoadingExternalParticipants } = useQuery({
		...trpc.admin.enpsFiles.getConfigExternalParticipants.queryOptions({
			enpsConfigId: enpsId || '',
			organizationId: organizationId || '',
		}),
		enabled: !!enpsId && !!organizationId && allowExternalParticipants,
	})

	// Delete external participant mutation
	const deleteExternalParticipantMutation = useMutation({
		...trpc.admin.enpsFiles.deleteConfigExternalParticipant.mutationOptions({
			onSuccess: () => {
				toast.showToast('Participant removed')
				queryClient.invalidateQueries({
					queryKey: trpc.admin.enpsFiles.getConfigExternalParticipants.queryKey({
						enpsConfigId: enpsId || '',
						organizationId: organizationId || '',
					}),
				})
			},
			onError: () => {
				toast.showToast('Failed to remove participant', 'error')
			},
		}),
	})

	// Update allowExternalParticipants when enpsConfig changes
	useEffect(() => {
		if (enpsConfig) {
			setAllowExternalParticipants(enpsConfig.allowExternalParticipants)
		}
	}, [enpsConfig])

	// Load population data into form
	useEffect(() => {
		if (populationData) {
			form.reset({
				population: {
					hierarchyLevels: populationData.hierarchyLevels || [],
					roles:
						populationData.roles?.map((r) => ({
							id: r.id,
							label: r.displayName || r.name,
						})) || [],
					blacklistedHierarchyRoles:
						populationData.blacklistedHierarchyRoles?.map((r) => ({
							id: r.id,
							label: r.displayName || r.name,
						})) || [],
					whitelistedUsers:
						populationData.whitelistedUsers?.map((u) => ({
							id: u.id,
							externalId: u.externalId,
							label: `${u.firstName} ${u.lastName}`,
						})) || [],
					blacklistedUsers:
						populationData.blacklistedUsers?.map((u) => ({
							id: u.id,
							externalId: u.externalId,
							label: `${u.firstName} ${u.lastName}`,
						})) || [],
				},
			})
		}
	}, [populationData, form])

	const handleDownloadTemplate = useCallback(() => {
		const template = 'email,firstName,lastName,company\njohn.doe@example.com,John,Doe,Acme Corp\n'
		const blob = new Blob([template], { type: 'text/csv' })
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = 'external_participants_template.csv'
		a.click()
		URL.revokeObjectURL(url)
	}, [])

	const handleDeleteParticipant = useCallback(
		(participantId: string) => {
			if (!enpsId || !organizationId) return
			deleteExternalParticipantMutation.mutate({
				participantId,
				enpsConfigId: enpsId,
				organizationId,
			})
		},
		[enpsId, organizationId, deleteExternalParticipantMutation],
	)

	const handleSubmit = form.handleSubmit(async (data) => {
		try {
			const populationInput = {
				hierarchyLevels: data.population.hierarchyLevels,
				roles: data.population.roles.map((r) => r.id),
				blacklistedHierarchyRoles: data.population.blacklistedHierarchyRoles.map((r) => r.id),
				whitelistedUsers: data.population.whitelistedUsers.map((u) => u.id),
				blacklistedUsers: data.population.blacklistedUsers.map((u) => u.id),
			}

			// Create or update population
			let populationId = enpsConfig?.populationId

			if (populationId) {
				// Update existing population
				await editPopulationMutation.mutateAsync({
					id: populationId,
					...populationInput,
				})
			} else {
				// Create new population
				const newPopulation = await createPopulationMutation.mutateAsync(populationInput)
				populationId = newPopulation.id
			}

			// Update eNPS config with populationId and allowExternalParticipants
			await editEnpsConfig({ populationId, allowExternalParticipants })
			form.reset(data)
			toast.showToast('Population saved successfully')
		} catch {
			toast.showToast('Failed to save population', 'error')
		}
	})

	// Use enpsId as cycleId substitute (the component needs it for role selection)
	const contextId = useMemo(() => enpsId || '', [enpsId])

	if (isLoadingPopulation) {
		return <SpinnerWithBackground />
	}

	return (
		<FormProvider {...form}>
			<form onSubmit={handleSubmit} className='relative pb-24 mt-6 w-full'>
				<div className='px-6 max-w-4xl space-y-8'>
					{/* Internal Users Section */}
					<div>
						<Text variant='medium' weight='semibold' className='mb-4'>
							Internal Users (OKTA)
						</Text>
						<Text variant='small' className='text-neutral-600 mb-6'>
							Define which employees will receive the NPS survey by selecting hierarchy levels and specific users. You
							can also exclude certain users from the population.
						</Text>

						<PopulationFields
							name='population'
							projection={{
								title: false,
								hierarchyTitle: true,
								hierarchyLevels: true,
								blacklistedHierarchyRoles: false,
								rolesTitle: false,
								roles: false,
								reviewersTitle: true,
								whitelistedUsers: true,
								blacklistedUsers: true,
							}}
							cycleId={contextId}
							organizationId={organizationId || ''}
							disabled={isSaving}
						/>
					</div>

					{/* External Users Section */}
					<div className='border-t border-gray-200 pt-8'>
						<div className='flex items-center justify-between mb-4'>
							<div>
								<Text variant='medium' weight='semibold'>
									External Users (No Login Required)
								</Text>
								<Text variant='small' className='text-neutral-600 mt-1'>
									Allow external users to participate via unique links (no OKTA login)
								</Text>
							</div>
							<Toggle checked={allowExternalParticipants} onCheckedChange={setAllowExternalParticipants} size='small' />
						</div>

						{allowExternalParticipants && (
							<div className='bg-gray-50 rounded-lg p-6 space-y-4'>
								<div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
									<Text variant='small' weight='semibold' className='text-blue-900'>
										External Participant Links
									</Text>
									<Text variant='xsmall' className='text-blue-700 mt-1'>
										External users will receive unique links valid for 90 days. They can submit responses without
										logging in. Participants uploaded here will be automatically included when you launch the survey.
									</Text>
								</div>

								<div className='flex items-center gap-4'>
									<Button variant='secondary' onClick={() => setIsUploadModalOpen(true)} type='button'>
										Upload CSV/Excel File
									</Button>
									<button
										type='button'
										onClick={handleDownloadTemplate}
										className='text-sm text-blue-600 hover:text-blue-800 underline'>
										Download Sample Template
									</button>
								</div>

								<Text variant='xsmall' className='text-neutral-500'>
									Supported formats: CSV, XLS, XLSX (max 2MB). Required columns: email, firstName, lastName. Optional:
									company
								</Text>

								{/* External Participants Table */}
								{isLoadingExternalParticipants ? (
									<div className='py-8'>
										<SpinnerWithBackground />
									</div>
								) : externalParticipants && externalParticipants.length > 0 ? (
									<div className='border border-gray-200 rounded-lg overflow-hidden'>
										<div className='bg-gray-100 px-4 py-2 flex items-center justify-between'>
											<Text variant='small' weight='semibold'>
												External Participants ({externalParticipants.length})
											</Text>
										</div>
										<div className='max-h-80 overflow-y-auto'>
											<table className='w-full text-sm'>
												<thead className='bg-gray-50 sticky top-0'>
													<tr>
														<th className='px-4 py-2 text-left font-medium text-gray-600'>Email</th>
														<th className='px-4 py-2 text-left font-medium text-gray-600'>Name</th>
														<th className='px-4 py-2 text-left font-medium text-gray-600'>Company</th>
														<th className='px-4 py-2 text-right font-medium text-gray-600'>Actions</th>
													</tr>
												</thead>
												<tbody className='divide-y divide-gray-200'>
													{externalParticipants.map((p) => (
														<tr key={p.id} className='hover:bg-gray-50'>
															<td className='px-4 py-2 text-gray-900'>{p.email}</td>
															<td className='px-4 py-2 text-gray-900'>
																{p.firstName} {p.lastName}
															</td>
															<td className='px-4 py-2 text-gray-500'>{p.company || '-'}</td>
															<td className='px-4 py-2 text-right'>
																<button
																	type='button'
																	onClick={() => handleDeleteParticipant(p.id)}
																	className='text-red-600 hover:text-red-800 p-1'
																	title='Remove participant'
																	disabled={deleteExternalParticipantMutation.isPending}>
																	<DeleteIcon className='w-4 h-4' />
																</button>
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									</div>
								) : (
									<div className='border border-dashed border-gray-300 rounded-lg p-8 text-center'>
										<Text variant='small' className='text-gray-500'>
											No external participants added yet. Upload a CSV or Excel file to add participants.
										</Text>
									</div>
								)}
							</div>
						)}
					</div>
				</div>

				<ActionBar
					rightActions={[
						{
							variant: 'secondary' as const,
							label: 'Back',
							onClick: () => navigate(`/organizations/${organizationId}/enps`),
						},
						{
							variant: 'primary' as const,
							label: isSaving ? 'Saving...' : 'Save & Continue',
							onClick: handleSubmit,
							disabled: isSaving,
						},
					]}
				/>
				<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
			</form>

			{/* Upload Modal */}
			{enpsId && (
				<EnpsExternalParticipantsUploadModal
					isOpen={isUploadModalOpen}
					onClose={() => setIsUploadModalOpen(false)}
					onUploadSuccess={() => {
						setIsUploadModalOpen(false)
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enpsFiles.getConfigExternalParticipants.queryKey({
								enpsConfigId: enpsId,
								organizationId: organizationId || '',
							}),
						})
					}}
					enpsConfigId={enpsId}
				/>
			)}
		</FormProvider>
	)
}
